/**
 * components/AckPill.jsx — Iter 32 Field Acknowledgment Loop surfaces.
 *
 * Restrained operational pills that surface on the plan-wall header, the
 * sheet-viewer top bar, and the Revision Review header. NEVER notifications.
 * NEVER a feed. Just inline operational awareness, like a chalk mark on a
 * site box: "this one's pending."
 *
 * Two flavours:
 *   <AckPill counts={...} onClick={...} variant="my|all|compact"/>
 *   <AckSummary counts={...} />  — full Pending/Ack'd/Clarif/Conflict line
 *
 * Counts shape (matches the backend):
 *   { pending, acknowledged, needs_clarification, field_conflict, total }
 */
import React from "react";
import { CheckCircle2, HelpCircle, AlertOctagon, Clock } from "lucide-react";


// State display config — order matters (operational priority).
export const ACK_STATES = [
  {
    key: "field_conflict",
    label: "Field Conflict",
    short: "Conflict",
    icon: AlertOctagon,
    color: "var(--st-escalated, #ef4444)",
    soft: "rgba(239, 68, 68, 0.10)",
  },
  {
    key: "needs_clarification",
    label: "Needs Clarification",
    short: "Clarif",
    icon: HelpCircle,
    color: "#f59e0b",
    soft: "rgba(245, 158, 11, 0.10)",
  },
  {
    key: "pending",
    label: "Pending Ack",
    short: "Pending",
    icon: Clock,
    color: "var(--fop-primary, #e85d1f)",
    soft: "rgba(232, 93, 31, 0.10)",
  },
  {
    key: "acknowledged",
    label: "Acknowledged",
    short: "Ack'd",
    icon: CheckCircle2,
    color: "#10b981",
    soft: "rgba(16, 185, 129, 0.10)",
  },
];

const STATE_BY_KEY = Object.fromEntries(ACK_STATES.map((s) => [s.key, s]));


/**
 * AckPill — the single most-prominent state worth surfacing in compact form.
 * Priority order (per ACK_STATES): conflict > clarification > pending > ack'd.
 * If counts is empty / undefined → renders nothing (we don't show "0 acks").
 */
export function AckPill({ counts, onClick, testid, scope = "all", className = "" }) {
  if (!counts || !counts.total) return null;

  // Find the highest-priority state with > 0 count.
  const cfg = ACK_STATES.find((s) => counts[s.key] > 0);
  if (!cfg) return null;
  const Icon = cfg.icon;
  const n = counts[cfg.key];

  // Friendlier copy when the field user is looking at their OWN ack vs the
  // aggregate ("Your ack pending" vs "3 pending"). `scope=mine` swaps the
  // label; `scope=all` shows aggregate counts.
  const label = scope === "mine" && n === 1
    ? `Your ${cfg.label.toLowerCase()}`
    : `${n} ${cfg.label}`;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-mono uppercase tracking-wider transition-colors border ${onClick ? "hover:brightness-110 cursor-pointer" : "cursor-default"} ${className}`}
      style={{
        color: cfg.color,
        background: cfg.soft,
        borderColor: cfg.color,
      }}
      data-testid={testid || `ack-pill-${cfg.key}`}
      data-ack-state={cfg.key}
      title={label}
    >
      <Icon size={10} />
      <span>{label}</span>
    </button>
  );
}


/**
 * AckSummary — restrained 1-line breakdown of all four states with their
 * counts. Used on the Revision Review header and the Plan Wall when the
 * user explicitly clicks for detail. Zero-counts are dimmed (visible but
 * never lying about activity that isn't there).
 */
export function AckSummary({ counts, className = "", testid = "ack-summary" }) {
  if (!counts) return null;
  return (
    <div
      className={`inline-flex items-center gap-3 text-[11px] font-mono uppercase tracking-wider ${className}`}
      data-testid={testid}
    >
      {ACK_STATES.map((s) => {
        const Icon = s.icon;
        const n = counts[s.key] || 0;
        const dim = n === 0;
        return (
          <span
            key={s.key}
            className="inline-flex items-center gap-1"
            style={{ color: dim ? "var(--fop-text-3)" : s.color, opacity: dim ? 0.55 : 1 }}
            data-testid={`${testid}-${s.key}`}
            data-count={n}
            title={`${n} ${s.label}`}
          >
            <Icon size={10} />
            <span>{n}</span>
            <span>{s.short}</span>
          </span>
        );
      })}
    </div>
  );
}


export { STATE_BY_KEY };
